// The three tables of the Project Overview. Every column header sorts; a row click
// shows the place on the map; Report buttons open the online report at that
// milepost. Column `key`s are the sort keys the PDF export understands.
import React, { useState } from 'react';
import { pct, shortManeuvers } from '../Safety/ManeuverLinks';
import { fmtUsd } from '../Safety/insightsText';
import { ANALYSES, ANALYSIS_COLORS, ANALYSIS_NAMES, SEVERITY_RANK, segmentName, sortRows } from './overviewModel';
import { clicks, openReport } from './OverviewStrips';
import { StatusChip } from '../SafetyOverview/SiteNoteDialog';
import { NOTE_STATUSES } from '../SafetyOverview/siteNotes';

const STATUS_ORDER = Object.fromEntries(NOTE_STATUSES.map(([k], i) => [k, i + 1]));

const fmt2 = (v) => (v == null ? '—' : Number(v).toFixed(2));

export const AnalysisTag = ({ analysis }) => (
  <span className={`ov-an ${analysis === 'GLR' ? 'ov-an-dark' : ''}`} style={{ background: ANALYSIS_COLORS[analysis] }}
    title={ANALYSIS_NAMES[analysis]}>{analysis}</span>
);
const Severity = ({ value }) => <span className={`safety-sev safety-sev-${value}`}>{value}</span>;

const ReportLink = ({ url, label, title }) => (url ? (
  <a className="ov-report" data-track="export.site_report" data-track-kind="export" href={url} target="_blank" rel="noreferrer" title={title}
    onClick={(e) => e.stopPropagation()}>{label} ↗</a>
) : <span className="safety-muted">—</span>);

function SortableTable({ columns, rows, sort, onSort, onRowClick, limit, rowKey, pins: heldPins, onPins, expand, onRowDoubleClick = null,
  limitPer = null }) {
  // Pinned rows ("tack") stay on top, in the order they were pinned, whatever the sort
  // and the row limit; a second click releases the row. The page may hold the pins
  // (`pins` + `onPins`, e.g. to keep them per overview); otherwise the table does.
  const [ownPins, setOwnPins] = useState([]);
  const pins = heldPins || ownPins;
  const setPins = onPins ? (fn) => onPins(fn(pins)) : setOwnPins;
  const accessors = Object.fromEntries(columns.filter((c) => c.sortValue).map((c) => [c.key, c.sortValue]));
  const byKey = new Map(rows.map((r) => [rowKey(r), r]));
  const pinned = pins.map((k) => byKey.get(k)).filter(Boolean);
  const pinnedKeys = new Set(pins);
  const ordered = sortRows(rows.filter((r) => !pinnedKeys.has(rowKey(r))), sort, accessors);
  // limitPer(row) -> group: the limit applies within each group (the Top N of each analysis)
  const perGroup = {};
  const limited = limit > 0
    ? (limitPer ? ordered.filter((r) => { const g = limitPer(r); perGroup[g] = (perGroup[g] || 0) + 1; return perGroup[g] <= limit; })
      : ordered.slice(0, limit))
    : ordered;
  const shown = [...pinned, ...limited];
  const togglePin = (key) => setPins((p) => (p.includes(key) ? p.filter((k) => k !== key) : [...p, key]));
  // optional row detail (`expand(row)` -> node): a ▸ button opens it under the row
  const [open, setOpen] = useState(() => new Set());
  const toggleOpen = (key) => setOpen((o) => { const n = new Set(o); if (n.has(key)) n.delete(key); else n.add(key); return n; });
  const width = columns.length + 1 + (expand ? 1 : 0);
  return (
    <>
      <div className="safety-table-scroll">
        <table className={`safety-table ${onRowClick ? 'safety-table-clickable' : ''}`}>
          <thead>
            <tr>
              <th className="ov-pin-col" title="Pin a row to the top of the table">{pins.length ? `${pins.length} pinned` : ''}</th>
              {expand && <th className="ov-pin-col" aria-label="Details" />}
              {columns.map((c) => (
                <th key={c.key} className={c.num ? 'ov-num' : ''} title={c.title}>
                  {c.sortValue ? (
                    <button type="button" className="ov-sort" title={`Sort by ${c.label}`}
                      onClick={() => onSort({ key: c.key, dir: sort?.key === c.key ? (sort.dir === 'asc' ? 'desc' : 'asc') : (c.num ? 'desc' : 'asc') })}>
                      {c.label}{sort?.key === c.key && <span className="ov-sort-arrow">{sort.dir === 'asc' ? '▲' : '▼'}</span>}
                    </button>
                  ) : c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {shown.map((r, i) => {
              const key = rowKey(r);
              const isPinned = pinnedKeys.has(key);
              const lastPinned = isPinned && i === pinned.length - 1 && shown.length > pinned.length;
              const isOpen = expand && open.has(key);
              return (
                <React.Fragment key={key}>
                <tr {...(onRowDoubleClick && onRowClick ? clicks(() => onRowClick(r), () => onRowDoubleClick(r))
                  : { onClick: onRowClick ? () => onRowClick(r) : undefined })}
                  className={`${isPinned ? 'ov-pinned' : ''} ${lastPinned ? 'ov-pinned-last' : ''}`}>
                  <td className="ov-pin-col">
                    <button type="button" className={`ov-pin ${isPinned ? 'on' : ''}`} aria-pressed={isPinned}
                      title={isPinned ? 'Release this row' : 'Keep this row on top'}
                      aria-label={isPinned ? 'Release this row' : 'Keep this row on top'}
                      onClick={(e) => { e.stopPropagation(); togglePin(key); }}>📌</button>
                  </td>
                  {expand && (
                    <td className="ov-pin-col">
                      <button type="button" className="ov-pin" aria-expanded={Boolean(isOpen)} aria-label={isOpen ? 'Hide details' : 'Show details'}
                        title={isOpen ? 'Hide details' : 'Show details'} onClick={(e) => { e.stopPropagation(); toggleOpen(key); }}>
                        <span className={`safety-section-chevron ${isOpen ? 'open' : ''}`}>▸</span>
                      </button>
                    </td>
                  )}
                  {columns.map((c) => <td key={c.key} className={c.num ? 'ov-num' : ''}>{c.render(r)}</td>)}
                </tr>
                {isOpen && <tr className="ov-expanded"><td colSpan={width}>{expand(r)}</td></tr>}
                </React.Fragment>
              );
            })}
            {!shown.length && <tr><td colSpan={width} className="safety-muted">Nothing matches the current filters.</td></tr>}
          </tbody>
        </table>
      </div>
      {limit > 0 && ordered.length > limited.length && (
        <div className="ov-more">Showing the top {limit}{limitPer ? ' of each analysis' : ''} ({limited.length} of {ordered.length}) by the current sort{pins.length ? `, plus ${pins.length} pinned` : ''}.</div>
      )}
    </>
  );
}

export function SummaryTable({ rows, analyses, measure, by, sort, onSort, onRowClick, limit = 0, pins, onPins, rank = null, labelHead = null }) {
  // rank / labelHead (New project): one row per hotspot or prospect, its label (P1) first
  const cell = (r, a) => {
    const v = r.byAnalysis[a]?.[measure === 'miles' ? 'miles' : 'sites'] || 0;
    return v ? (measure === 'miles' ? v.toFixed(2) : v) : <span className="ov-faint">–</span>;
  };
  const columns = [
    ...(rank ? [{ key: 'rank', label: '#', num: true, sortValue: (r) => rank.get(r.key) ?? 1e9,
      render: (r) => <span className="so-hs-pill">{r.rankLabel}</span> }] : []),
    { key: 'label', label: labelHead || (by === 'road' ? 'Road' : 'Road · segment'), sortValue: (r) => [r.road, r.segment || 0], render: (r) => r.label },
    { key: 'ranges', label: 'Mileposts', render: (r) => <span className="safety-muted">{r.ranges.map(([a, b]) => `${a.toFixed(1)}–${b.toFixed(1)}`).join(', ')}</span> },
    { key: 'miles', label: 'Surveyed mi', num: true, title: 'Directional miles: each surveyed direction counts', sortValue: (r) => r.miles, render: (r) => r.miles.toFixed(1) },
    ...analyses.map((a) => ({
      key: a, label: a, num: true, title: ANALYSIS_NAMES[a],
      sortValue: (r) => r.byAnalysis[a]?.[measure === 'miles' ? 'miles' : 'sites'] || 0, render: (r) => cell(r, a),
    })),
    { key: 'total', label: measure === 'miles' ? 'Deficient mi' : 'Sites', num: true,
      sortValue: (r) => (measure === 'miles' ? r.deficientMi : r.sites),
      render: (r) => <span className="safety-key">{measure === 'miles' ? r.deficientMi.toFixed(2) : r.sites}</span> },
    { key: 'severe', label: 'Severe', num: true, sortValue: (r) => r.severe, render: (r) => r.severe },
    { key: 'pct', label: '% deficient', num: true, title: 'Deficient length over surveyed directional miles; overlapping sites count once',
      sortValue: (r) => r.pct, render: (r) => `${r.pct.toFixed(1)}%` },
  ];
  return <SortableTable columns={columns} rows={rows} sort={sort} onSort={onSort} onRowClick={onRowClick} limit={limit}
    rowKey={(r) => r.key} pins={pins} onPins={onPins} />;
}

/** The rank column (Safety Overview): the rank the map's Top N shares, and a mark on the rows on the map.
 *  order (optional): the sort position when it is not the rank itself (ranks kept per analysis);
 *  coloured: the rank on its analysis' colour, as on the map. */
const rankColumn = (rank, onMap, title, order = null, coloured = false) => ({
  key: 'rank', label: '#', num: true, title,
  sortValue: (r) => (order || rank).get(r.id) ?? 1e9,
  render: (r) => (rank.has(r.id) ? (
    <span className="so-rank">
      {coloured
        ? <span className={`so-rank-pill ${r.analysis === 'GLR' ? 'so-rank-pill-dark' : ''}`} style={{ background: ANALYSIS_COLORS[r.analysis] }}>{rank.get(r.id)}</span>
        : `#${rank.get(r.id)}`}
      {onMap && onMap.has(r.id) ? <span className="so-onmap" title="Shown on the map">◉</span> : null}
    </span>
  ) : '—'),
});

export function ClustersTable({ rows, position, axis, unify, sort, onSort, onRowClick, limit, pins, onPins, rank = null, onMap = null, rankTitle }) {
  const columns = [
    ...(rank ? [rankColumn(rank, onMap, rankTitle)] : []),
    { key: 'road', label: unify ? 'Road' : 'Road · segment', sortValue: (c) => [c.road, c.segment || 0], render: (c) => (unify ? c.road : segmentName(c)) },
    { key: 'position', label: axis === 'mi' ? 'Mileage' : 'Milepost', num: true, sortValue: (c) => position(c.segKey, c.mpStart),
      render: (c) => (axis === 'mi' ? `${fmt2(position(c.segKey, c.mpStart))} mi` : `MP ${fmt2(c.mpStart)}`) },
    { key: 'spanMi', label: 'Span (mi)', num: true, sortValue: (c) => c.spanMi, render: (c) => c.spanMi.toFixed(2) },
    { key: 'n', label: 'Analyses', num: true, sortValue: (c) => [c.n, SEVERITY_RANK[c.severity] || 0, c.sites], render: (c) => <span className="safety-key">{c.n}</span> },
    { key: 'which', label: 'Which', render: (c) => c.analyses.map((a) => <AnalysisTag key={a} analysis={a} />) },
    { key: 'sites', label: 'Sites', num: true, sortValue: (c) => c.sites, render: (c) => c.sites },
    { key: 'severity', label: 'Worst severity', sortValue: (c) => SEVERITY_RANK[c.severity] || 0, render: (c) => <Severity value={c.severity} /> },
    { key: 'reports', label: 'Open report at this milepost',
      render: (c) => (Object.keys(c.reports).length ? (
        <span className="ov-report-row">
          {Object.entries(c.reports).map(([a, site]) => (
            <ReportLink key={a} url={site.reportUrl} label={a} title={`Open the ${a} report at MP ${fmt2(site.mpStart)}`} />
          ))}
        </span>
      ) : <span className="safety-muted">—</span>) },
  ];
  return <SortableTable columns={columns} rows={rows} sort={sort} onSort={onSort} onRowClick={onRowClick} limit={limit}
    rowKey={(c) => c.id} pins={pins} onPins={onPins} />;
}

/** A score: a click shows how it was reached. */
const ScoreButton = ({ value, onClick }) => (
  <button type="button" className="ov-report" title="How this score was reached"
    onClick={(e) => { e.stopPropagation(); onClick(); }}>{Math.round(value).toLocaleString('en-US')}</button>
);

export function SitesTable({ rows, position, axis, sort, onSort, onRowClick, limit, pins, onPins, crashMode = false, expand,
  rank = null, onMap = null, rankTitle, rankOrder = null, perAnalysis = false, notes = null, onNote = null, group = null, rowFlags = null,
  onScore = null, scoreOf = null }) {
  // onScore / scoreOf (Safety Overview): a Score column; a click shows how the score was reached
  // rowFlags: {site id: {pct}} — a Right of way column for the sight-distance sites
  // group (New project): {head, of(row) -> 'P1', rank(row) -> n} — the hotspot of each row, first column; the row limit is per group
  // notes / onNote (site notes): {site id: note} and the editor — a Status column with the note behind it
  // perAnalysis: ranks are kept per analysis — the # is on the analysis colour and the row limit is per analysis
  // double-click a row: its report (a single click still shows the site on the map)
  const onRowDoubleClick = (r) => openReport(r.reportUrl);
  const columns = [
    ...(group ? [{ key: 'group', label: group.head, num: true, sortValue: (r) => group.rank(r),
      render: (r) => <span className="so-hs-pill">{group.of(r)}</span> }] : []),
    ...(rank ? [rankColumn(rank, onMap, rankTitle, rankOrder, true)] : []),
    { key: 'road', label: 'Road', sortValue: (r) => [r.road, r.segment || 0], render: (r) => r.road },
    { key: 'segment', label: 'Segment', sortValue: (r) => r.segment || 0, render: (r) => r.segment || '—' },
    { key: 'analysis', label: 'Analysis', sortValue: (r) => ANALYSES.indexOf(r.analysis), render: (r) => <AnalysisTag analysis={r.analysis} /> },
    { key: 'direction', label: 'Dir', title: 'P = positive (increasing milepost), N = negative; P N = one curve, both directions',
      sortValue: (r) => r.direction, render: (r) => (r.directions || [r.direction]).join(' ') },
    { key: 'position', label: axis === 'mi' ? 'Mileage' : 'Milepost', num: true, sortValue: (r) => position(r.segKey, r.mpStart),
      render: (r) => (axis === 'mi' ? `${fmt2(position(r.segKey, r.mpStart))} mi` : `MP ${fmt2(r.mpStart)}`) },
    { key: 'lengthMi', label: 'Length (mi)', num: true, sortValue: (r) => r.lengthMi || 0, render: (r) => (r.lengthMi ? r.lengthMi.toFixed(2) : '—') },
    { key: 'severity', label: 'Severity', sortValue: (r) => SEVERITY_RANK[r.severity] || 0, render: (r) => <Severity value={r.severity} /> },
    { key: 'worstText', label: 'Worst value', sortValue: (r) => r.worstText || '', render: (r) => r.worstText || '—' },
    ...(onScore && scoreOf ? [{ key: 'score', label: 'Score', num: true, title: 'What the rank orders by; click to see how it was reached',
      sortValue: (r) => scoreOf(r), render: (r) => <ScoreButton value={scoreOf(r)} onClick={() => onScore(r)} /> }] : []),
    ...(crashMode ? [
      { key: 'crashCount', label: 'Crashes', num: true, title: 'Crashes within 0.1 mi of the site (the crash filters apply)',
        sortValue: (r) => r.crashCount || 0, render: (r) => r.crashCount || <span className="ov-faint">0</span> },
      { key: 'injuryCount', label: 'K+Inj', num: true, title: 'Fatal and injury crashes among them (animal strikes excluded)',
        sortValue: (r) => r.injuryCount || 0, render: (r) => r.injuryCount || <span className="ov-faint">0</span> },
    ] : []),
    ...(crashMode && rows.some((r) => r.cmf) ? [
      { key: 'cmf', label: 'Countermeasure', title: 'Best-rated FHWA CMF Clearinghouse countermeasure under the quality policy; ▸ for the details',
        sortValue: (r) => r.cmf?.entry?.name || '', render: (r) => (r.cmf?.entry ? r.cmf.entry.name : <span className="safety-muted" title={r.cmf?.note || ''}>—</span>) },
      { key: 'benefit', label: 'Benefit / yr', num: true, title: 'Crashes prevented per year × their KABCO unit cost',
        sortValue: (r) => r.cmf?.benefitPerYr || 0, render: (r) => (r.cmf?.benefitPerYr ? <span className="safety-benefit">{fmtUsd(r.cmf.benefitPerYr)}</span> : <span className="ov-faint">–</span>) },
      { key: 'bc', label: 'B/C', num: true, title: 'Present value of the benefit over the service life ÷ unit cost',
        sortValue: (r) => r.cmf?.bcRatio || 0, render: (r) => (r.cmf?.bcRatio != null ? r.cmf.bcRatio : <span className="ov-faint">–</span>) },
    ] : []),
    { key: 'projectId', label: 'Project', num: true, sortValue: (r) => r.projectId || 0, render: (r) => (r.projectId ? `#${r.projectId}` : '—') },
    ...(rowFlags && Object.keys(rowFlags).length ? [{
      key: 'row', label: 'Right of way', num: true,
      title: 'How much of the most exposed blocked sight line lies inside the state right of way (approximate polygons; up to 15 ft outside counts as inside). 100% is all of it.',
      sortValue: (r) => (rowFlags[r.id] ? rowFlags[r.id].pct : 101),
      render: (r) => {
        const f = rowFlags[r.id];
        if (!f) return <span className="ov-faint">–</span>;
        return f.pct < 100
          ? <span className="so-row-chip part" title="Part of the sight line is outside the right of way: the obstruction may be on private land">{f.pct}%</span>
          : <span className="so-row-chip all" title="All of the sight line is inside the right of way: the obstruction is likely on state land">100%</span>;
      },
    }] : []),
    ...(onNote ? [{
      key: 'note', label: 'Status / note', title: 'The handling status and note kept on the site (everyone with access to the road sees them)',
      sortValue: (r) => (notes && notes[r.id] ? STATUS_ORDER[notes[r.id].status] || 9 : 99),
      render: (r) => {
        const n = notes && notes[r.id];
        return (
          <span className="so-note-cell" title={n ? `${n.note || ''}\n— ${n.author}, ${n.at}` : 'Add a note'}>
            {n && <StatusChip status={n.status} />}
            {n && n.note && <span className="so-note-snip">{n.note.length > 40 ? `${n.note.slice(0, 40)}…` : n.note}</span>}
            <button type="button" className="ov-pin" aria-label={n ? 'Edit the note' : 'Add a note'}
              onClick={(e) => { e.stopPropagation(); onNote(r); }}><i className="fa-solid fa-pen-to-square" aria-hidden="true" /></button>
          </span>
        );
      },
    }] : []),
    { key: 'report', label: 'Report', title: 'ISD: one report per maneuver that is short of the required distance, worst first',
      render: (r) => {
        const short = shortManeuvers(r.maneuvers).filter((m) => m.reportUrl);
        if (!short.length) return <ReportLink url={r.reportUrl} label="Report" title={`Open the ${r.analysis} report at MP ${fmt2(r.mpStart)}`} />;
        return (
          <span className="ov-report-row">
            {short.map((m) => <ReportLink key={m.id} url={m.reportUrl} label={`${m.name} ${pct(m.quality)}`} title={`${m.id} · ${m.label || ''}`} />)}
          </span>
        );
      } },
  ];
  return <SortableTable columns={columns} rows={rows} sort={sort} onSort={onSort} onRowClick={onRowClick} limit={limit}
    rowKey={(r) => r.id} pins={pins} onPins={onPins} expand={expand} onRowDoubleClick={onRowDoubleClick}
    limitPer={group ? group.of : perAnalysis ? (r) => r.analysis : null} />;
}

/** Deficiency hotspots (New project spawning): stretches where deficiency sites follow each other closely,
 *  ranked by the sum of their sites' scores. A row click shows the hotspot (and narrows the sites table to it). */
export function HotspotsTable({ rows, rank, sort, onSort, onRowClick, limit, pins, onPins, selected = null, prefix = 'H', onScore = null }) {
  // prefix: H (hotspots) or P (project prospects)
  const columns = [
    { key: 'rank', label: '#', num: true, title: 'Rank: the sum of the scores of its sites, worst first',
      sortValue: (h) => rank.get(h.id) ?? 1e9,
      render: (h) => <span className={`so-hs-pill ${selected === h.id ? 'on' : ''}`}>{prefix}{rank.get(h.id)}</span> },
    { key: 'road', label: 'Road · segment', sortValue: (h) => [h.road, h.segment || 0], render: (h) => (h.segment ? `${h.road} · ${h.segment}` : h.road) },
    { key: 'dir', label: 'Dir', title: 'P = positive, N = negative; P N = both directions together',
      sortValue: (h) => h.directions.join(''), render: (h) => h.directions.join(' ') },
    { key: 'mp', label: 'Mileposts', num: true, sortValue: (h) => h.mpStart, render: (h) => `${fmt2(h.mpStart)}–${fmt2(h.mpEnd)}` },
    { key: 'lengthMi', label: 'Length (mi)', num: true, sortValue: (h) => h.lengthMi,
      render: (h) => (h.short
        ? <span title={`The road or search area is shorter than the ${h.requestedMi} mi project length: this stretch is all of it`}>{h.lengthMi.toFixed(2)} <span className="so-short-tag">shorter than {h.requestedMi} mi</span></span>
        : h.lengthMi.toFixed(2)) },
    { key: 'n', label: 'Sites', num: true, sortValue: (h) => h.n, render: (h) => <span className="safety-key">{h.n}</span> },
    { key: 'analyses', label: 'Analyses', sortValue: (h) => h.analyses.length, render: (h) => h.analyses.map((a) => <AnalysisTag key={a} analysis={a} />) },
    { key: 'severe', label: 'Severe', num: true, sortValue: (h) => h.severe, render: (h) => h.severe },
    { key: 'perMi', label: 'Sites / mi', num: true, title: 'Sites per mile of the stretch (counted as at least 0.1 mi)', sortValue: (h) => h.perMi, render: (h) => h.perMi.toFixed(1) },
    { key: 'score', label: 'Score', num: true, title: 'The sum of the scores of the sites inside; click to see how it was reached', sortValue: (h) => h.score,
      render: (h) => (onScore ? <ScoreButton value={h.score} onClick={() => onScore(h)} /> : Math.round(h.score).toLocaleString('en-US')) },
  ];
  return <SortableTable columns={columns} rows={rows} sort={sort} onSort={onSort} onRowClick={onRowClick} limit={limit}
    rowKey={(h) => h.id} pins={pins} onPins={onPins} />;
}

/** Crash concentrations (2+ crashes within 0.3 mi) and whether a deficiency site sits under
 *  them — a cluster with none is an analysis blind spot. Rows come from the page. */
export function CrashClustersTable({ rows, sort, onSort, onRowClick, limit, pins, onPins }) {
  const columns = [
    { key: 'road', label: 'Road · segment', sortValue: (c) => c.road, render: (c) => c.road },
    { key: 'mpStart', label: 'Milepost', num: true, sortValue: (c) => c.mpStart,
      render: (c) => `MP ${fmt2(c.mpStart)}${c.mpEnd > c.mpStart ? `–${fmt2(c.mpEnd)}` : ''}` },
    { key: 'count', label: 'Crashes', num: true, sortValue: (c) => c.count, render: (c) => <span className="safety-key">{c.count}</span> },
    { key: 'ki', label: 'K+Inj', num: true, title: 'Fatal and injury crashes', sortValue: (c) => c.ki, render: (c) => c.ki },
    { key: 'hit', label: 'Deficiency site', title: 'A shown deficiency site within 0.1 mi; none = the analyses miss this crash cluster',
      sortValue: (c) => (c.hit ? 1 : 0),
      render: (c) => <span className={c.hit ? 'safety-chip safety-chip-bad' : 'safety-chip safety-chip-neutral'}>{c.hit ? c.hitAnalyses.join(' · ') : 'none'}</span> },
    { key: 'leading', label: 'Leading type', sortValue: (c) => c.leading || '', render: (c) => c.leading || '—' },
    { key: 'towns', label: 'Towns', render: (c) => c.towns || '—' },
  ];
  return <SortableTable columns={columns} rows={rows} sort={sort} onSort={onSort} onRowClick={onRowClick} limit={limit}
    rowKey={(c) => c.id} pins={pins} onPins={onPins} />;
}
